// O RELATÓRIO DE CONVERSÃO E O DE GERAÇÃO — o preço da tradução, na tela.
//
// Traduzir custa informação. A decisão registrada no ADR-0007 é que esse custo
// fica VISÍVEL na hora, e não seis meses depois numa conciliação que não fecha:
// nenhuma linha de origem some sem constar aqui com um motivo.
//
// Duas coisas guiaram a forma:
//
//  1. CONTAGEM PRIMEIRO, LISTA DEPOIS. Quem sobe o arquivo quer saber "entrou
//     tudo?" em um segundo. As listas longas (descartadas, derivados) ficam
//     recolhidas — abrir é uma decisão, não um scroll obrigatório.
//  2. DERIVADO DE DESLIGAMENTO É DIFERENTE DO RESTO. Ele não é um dado do
//     arquivo: é uma conclusão que o tradutor tirou de duas ausências, e o
//     efeito é irreversível. Por isso tem bloco próprio, mostra a REGRA que
//     levou à conclusão, e é o único que avisa a tela de que foi aberto
//     (`onDerivadosVistos`) — o botão de enviar depende disso.
//  3. NADA AQUI É JSON NA TELA. Toda contagem, lista com motivo, campo
//     derivado e soma origem × gerado é painel ou tabela — a leitura é a
//     entrega, não um despejo do payload. O JSON continua ao alcance de quem
//     investiga com a engenharia, mas como `ghost` no fim do painel.

import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, Modal, Pill, type Column } from "@atlas/ui/web";
import type { ArquivoTrocado, LayoutSituacao, RelatorioConversao, RelatorioGeracao } from "@atlas/sdk";
import { LayoutChip, ehCodigoPadrao } from "../../components/LayoutChip";
import { downloadJson } from "../../lib/csv";

const fmtNum = (n: number) => n.toLocaleString("pt-BR");
const fmtValor = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// ============================================================ peças comuns

/**
 * A parte da identidade do layout que o relatório NÃO carrega.
 *
 * O payload guarda só código e versão — foi o que ficou gravado na hora da
 * tradução, e é de propósito: reler o passado com o catálogo de hoje é
 * exatamente o que a vigência por competência existe para impedir. Nome e
 * situação vivem no catálogo, e quem abre o painel já os tem em mãos (o
 * registro do histórico, a unidade escolhida). Vir por prop evita uma consulta
 * a mais só para escrever o nome — e evita fazer a prefeitura depender de uma
 * rota de catálogo que ela não enxerga.
 */
export interface IdentidadeLayout {
  nome?: string;
  situacao?: LayoutSituacao;
  padrao?: boolean;
}

function Cabecalho({
  titulo,
  layout,
  identidade,
  semTraducao,
  competencia,
}: {
  titulo: string;
  layout: { codigo: string; versao: string };
  identidade?: IdentidadeLayout;
  semTraducao?: boolean;
  competencia: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
      <strong style={{ fontSize: 14 }}>{titulo}</strong>
      {/* A MESMA IDENTIDADE DE LAYOUT DE TODA TELA (FR-036): nome · código ·
          versão · situação, na mesma ordem do hub, da ficha e da coluna Layout.
          Sem `comLink` — a ficha vive sob /averbadora, e este painel também é
          renderizado no portal da prefeitura, onde o link daria 403. */}
      <LayoutChip
        tamanho="sm"
        nome={identidade?.nome ?? layout.codigo}
        codigo={layout.codigo}
        versao={layout.versao}
        situacao={identidade?.situacao}
        padrao={identidade?.padrao ?? (semTraducao === true || ehCodigoPadrao(layout.codigo))}
      />
      <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{competencia}</span>
      {semTraducao === undefined ? null : semTraducao ? (
        <Pill variant="averbado">sem tradução</Pill>
      ) : (
        <Pill variant="pendente">traduzido</Pill>
      )}
    </div>
  );
}

/**
 * A SAÍDA DE EMERGÊNCIA, e nada além disso.
 *
 * O relatório existe para ser lido na tela: quem opera a folha não abre JSON.
 * Mas quem investiga um caso junto da engenharia às vezes precisa do payload
 * inteiro, e negar isso empurraria a conversa para "me manda um print". Por
 * isso o botão existe — `ghost`, no fim, depois de tudo o que responde a
 * pergunta sem download nenhum.
 */
function BaixarJson({ nome, dados }: { nome: string; dados: unknown }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
      <Button
        size="sm"
        variant="ghost"
        title="O relatório inteiro como veio da API. Serve para anexar num chamado — a leitura está acima."
        onClick={() => downloadJson(nome, dados)}
      >
        Baixar JSON
      </Button>
    </div>
  );
}

/** Contagem grande com rótulo. É o que responde "entrou tudo?" num relance. */
function Numero({ rotulo, valor, tom }: { rotulo: string; valor: number; tom?: "alerta" }) {
  const cor = tom === "alerta" && valor > 0 ? "var(--warn-500)" : "var(--text)";
  return (
    <div style={{ minWidth: 96 }}>
      <div style={{ fontSize: 19, fontWeight: 700, color: cor, lineHeight: 1.1 }}>{fmtNum(valor)}</div>
      <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{rotulo}</div>
    </div>
  );
}

/**
 * Seção recolhível. Some quando não tem conteúdo — seção vazia é ruído.
 *
 * `onAbrir` existe por causa do portão de desligamento derivado: a tela precisa
 * saber que a lista foi REALMENTE aberta, não que ela estava na página.
 */
function Secao({
  titulo,
  contagem,
  children,
  destaque,
  onAbrir,
}: {
  titulo: string;
  contagem: number;
  children: ReactNode;
  destaque?: boolean;
  onAbrir?: () => void;
}) {
  const [aberta, setAberta] = useState(false);
  if (contagem === 0) return null;
  const cor = destaque ? "var(--warn-500)" : "var(--border)";
  return (
    <div
      style={{
        marginTop: 10,
        border: `1px solid color-mix(in srgb, ${cor} ${destaque ? "60%" : "100%"}, transparent)`,
        borderRadius: 8,
      }}
    >
      <button
        type="button"
        aria-expanded={aberta}
        onClick={() => {
          const proxima = !aberta;
          setAberta(proxima);
          if (proxima) onAbrir?.();
        }}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 10px",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          font: "inherit",
          textAlign: "left",
          color: destaque ? "var(--warn-500)" : "var(--text)",
        }}
      >
        <span style={{ fontSize: 11, width: 10 }}>{aberta ? "▾" : "▸"}</span>
        <strong style={{ fontSize: 13 }}>{titulo}</strong>
        <span style={{ fontSize: 12, color: "var(--text-dim)" }}>({fmtNum(contagem)})</span>
      </button>
      {aberta ? <div style={{ padding: "0 10px 10px" }}>{children}</div> : null}
    </div>
  );
}

// ============================================================ conversão (entrada)

type Descartada = RelatorioConversao["descartadas"][number];
type Agregada = RelatorioConversao["agregadas"][number];
type Derivado = RelatorioConversao["derivados"][number];
type Normalizado = RelatorioConversao["normalizados"][number];
type NaoInformavel = RelatorioConversao["naoInformaveis"][number];

const COLUNAS_DESCARTADAS: Column<Descartada>[] = [
  { key: "arquivo", header: "Arquivo" },
  { key: "linha", header: "Linha", align: "right", mono: true },
  { key: "matricula", header: "Matrícula", mono: true, render: (d) => d.matricula ?? "—" },
  { key: "motivo", header: "Motivo", wrap: true },
];

const COLUNAS_AGREGADAS: Column<Agregada>[] = [
  { key: "matricula", header: "Matrícula", mono: true },
  { key: "codigoVerba", header: "Verba", mono: true },
  { key: "contratosDeclarados", header: "Contratos", align: "right" },
  { key: "valor", header: "Valor", align: "right", render: (a) => fmtValor(a.valor) },
];

/**
 * Os DADOS que sustentam uma conclusão, em pares rotulados.
 *
 * Vinha como `motivo: FALECIDO · situacao: 9` numa linha só — texto colado que
 * o operador tinha de decifrar. Cada par é o que o tradutor leu do arquivo para
 * concluir o que concluiu; separado, dá para conferir contra a folha.
 */
function Pares({ dados }: { dados: Record<string, string> }) {
  const pares = Object.entries(dados);
  if (pares.length === 0) return <span style={{ opacity: 0.4 }}>—</span>;
  return (
    <div style={{ display: "grid", gap: 2 }}>
      {pares.map(([chave, valor]) => (
        <span key={chave} style={{ fontSize: 11 }}>
          <span style={{ color: "var(--text-dim)" }}>{chave}:</span>{" "}
          <b style={{ fontFamily: "var(--font-mono, monospace)" }}>{valor}</b>
        </span>
      ))}
    </div>
  );
}

const COLUNAS_DESLIGAMENTOS: Column<Derivado>[] = [
  { key: "matricula", header: "Matrícula", mono: true, width: 110 },
  { key: "regra", header: "Regra que levou à conclusão", wrap: true },
  { key: "dados", header: "O que o arquivo dizia", render: (d) => <Pares dados={d.dados} /> },
];

const COLUNAS_DERIVADOS: Column<Derivado>[] = [
  { key: "matricula", header: "Matrícula", mono: true, width: 110 },
  { key: "tipo", header: "Campo", width: 110, render: (d) => <Pill variant="expirado">{d.tipo}</Pill> },
  { key: "regra", header: "Regra que levou à conclusão", wrap: true },
  { key: "dados", header: "O que o arquivo dizia", render: (d) => <Pares dados={d.dados} /> },
];

const COLUNAS_NORMALIZADOS: Column<Normalizado>[] = [
  { key: "campo", header: "Campo", mono: true, width: 150 },
  { key: "regra", header: "Correção aplicada", wrap: true },
  { key: "quantidade", header: "Linhas", align: "right", render: (n) => fmtNum(n.quantidade) },
];

const COLUNAS_NAO_INFORMAVEIS: Column<NaoInformavel>[] = [
  { key: "campo", header: "Campo do modelo", mono: true, width: 150 },
  { key: "motivo", header: "Por que não veio", wrap: true },
  { key: "linhasAfetadas", header: "Linhas afetadas", align: "right", render: (n) => fmtNum(n.linhasAfetadas) },
];

/**
 * As somas dos dois lados numa tabela só.
 *
 * As chaves de origem e de gerado NÃO são as mesmas — origem fala a linguagem
 * do arquivo do ente (`folha_pagofp`), gerado fala a do padrão
 * (`consignados_valor`). Somar ou subtrair uma da outra seria inventar uma
 * conferência que não existe. O que a tabela entrega é a comparação possível:
 * os dois lados lado a lado, com o lado explícito em cada linha.
 */
interface LinhaTotal {
  lado: "origem" | "gerado";
  chave: string;
  valor: number;
}

function totaisEmLinhas(totais: RelatorioConversao["totais"]): LinhaTotal[] {
  return [
    ...Object.entries(totais.origem).map(([chave, valor]) => ({ lado: "origem" as const, chave, valor })),
    ...Object.entries(totais.gerado).map(([chave, valor]) => ({ lado: "gerado" as const, chave, valor })),
  ];
}

const COLUNAS_TOTAIS: Column<LinhaTotal>[] = [
  {
    key: "lado",
    header: "Lado",
    width: 110,
    render: (t) => (
      <Pill variant={t.lado === "origem" ? "expirado" : "emdia"}>
        {t.lado === "origem" ? "origem" : "gerado"}
      </Pill>
    ),
  },
  { key: "chave", header: "Total", mono: true, wrap: true },
  { key: "valor", header: "Soma", align: "right", render: (t) => fmtNum(t.valor) },
];

/**
 * O relatório de CONVERSÃO — o que a tradução leu, gerou, descartou, derivou e
 * não soube dizer.
 *
 * `onDerivadosVistos` dispara quando a lista de desligamentos derivados é
 * aberta. É o sinal que libera o envio do pacote (FR-019).
 */
export function RelatorioConversaoPainel({
  relatorio,
  identidade,
  onDerivadosVistos,
}: {
  relatorio: RelatorioConversao;
  identidade?: IdentidadeLayout;
  onDerivadosVistos?: () => void;
}) {
  const lidas = relatorio.lidos.reduce((s, x) => s + x.linhas, 0);
  const geradas = relatorio.gerados.reduce((s, x) => s + x.linhas, 0);
  const desligamentos = relatorio.derivados.filter((d) => d.tipo === "desligamento");
  const outrosDerivados = relatorio.derivados.filter((d) => d.tipo !== "desligamento");

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
      <Cabecalho
        titulo="Relatório de conversão"
        layout={relatorio.layout}
        identidade={identidade}
        semTraducao={relatorio.semTraducao}
        competencia={relatorio.competencia}
      />

      {relatorio.semTraducao ? (
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-dim)" }}>
          O pacote já veio no padrão Atlas. Nada foi traduzido, e por isso não há linha descartada, campo
          derivado nem informação perdida a relatar.
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", padding: "6px 0" }}>
        <Numero rotulo="linhas lidas" valor={lidas} />
        <Numero rotulo="linhas geradas" valor={geradas} />
        <Numero rotulo="descartadas" valor={relatorio.descartadas.length} tom="alerta" />
        <Numero rotulo="derivadas" valor={relatorio.derivados.length} tom="alerta" />
        <Numero rotulo="agregadas" valor={relatorio.agregadas.length} />
        <Numero rotulo="não informáveis" valor={relatorio.naoInformaveis.length} />
      </div>

      <Secao
        titulo="Lidos e gerados, por arquivo"
        contagem={relatorio.lidos.length + relatorio.gerados.length}
      >
        <div
          style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
        >
          <div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 4 }}>
              Lidos (arquivo de origem)
            </div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
              {relatorio.lidos.map((x) => (
                <li key={x.arquivo}>
                  {x.arquivo}: <b>{fmtNum(x.linhas)}</b>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 4 }}>
              Gerados (pacote padrão)
            </div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
              {relatorio.gerados.map((x) => (
                <li key={x.arquivo}>
                  {x.arquivo}: <b>{fmtNum(x.linhas)}</b>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Secao>

      <Secao titulo="Linhas descartadas" contagem={relatorio.descartadas.length} destaque>
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-dim)" }}>
          Cada uma destas linhas <b>não virou dado</b>. Nenhuma some sem motivo.
        </p>
        <DataTable
          caption="Linhas do arquivo de origem que a tradução não converteu, com arquivo, linha, matrícula e motivo"
          columns={COLUNAS_DESCARTADAS}
          rows={relatorio.descartadas}
          rowKey={(d) => `${d.arquivo}-${d.linha}-${d.motivo}`}
        />
      </Secao>

      {/* DESLIGAMENTO DERIVADO EM BLOCO PRÓPRIO. Ele decide se o envio pode
          sair, e enterrá-lo junto de "normalizados" seria esconder o único item
          da lista cujo efeito é irreversível. */}
      <Secao
        titulo="Desligamentos DERIVADOS — exigem sua confirmação"
        contagem={desligamentos.length}
        destaque
        onAbrir={onDerivadosVistos}
      >
        <p style={{ margin: "0 0 8px", fontSize: 12 }}>
          Nenhum destes veio declarado no arquivo: a Atlas <b>concluiu</b> cada um pela regra abaixo. Desligar
          tira o contrato da folha e notifica a consignatária, e <b>não é reversível</b>. Confira antes de enviar.
        </p>
        <DataTable
          caption="Desligamentos que a tradução concluiu, com a matrícula, a regra aplicada e o que o arquivo dizia"
          columns={COLUNAS_DESLIGAMENTOS}
          rows={desligamentos}
          rowKey={(d) => `${d.matricula}-${d.regra}`}
        />
      </Secao>

      <Secao titulo="Outros campos derivados" contagem={outrosDerivados.length}>
        <DataTable
          caption="Campos que a tradução concluiu sem estarem declarados, com a matrícula, o campo e a regra"
          columns={COLUNAS_DERIVADOS}
          rows={outrosDerivados}
          rowKey={(d) => `${d.matricula}-${d.tipo}-${d.regra}`}
        />
      </Secao>

      <Secao titulo="Campos normalizados" contagem={relatorio.normalizados.length}>
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-dim)" }}>
          Corrigidos para caber no modelo. O dado continua sendo o do arquivo.
        </p>
        <DataTable
          caption="Campos corrigidos para caber no modelo, com a correção aplicada e quantas linhas ela alcançou"
          columns={COLUNAS_NORMALIZADOS}
          rows={relatorio.normalizados}
          rowKey={(n) => `${n.campo}-${n.regra}`}
        />
      </Secao>

      <Secao titulo="Linhas agregadas" contagem={relatorio.agregadas.length}>
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-dim)" }}>
          Uma linha do seu arquivo somando mais de uma dívida da Atlas. O casamento cai no caminho por
          atributos, e o empate vira pendência de conciliação.
        </p>
        <DataTable
          caption="Linhas do arquivo que somaram mais de uma dívida, com matrícula, verba, contratos e valor"
          columns={COLUNAS_AGREGADAS}
          rows={relatorio.agregadas}
          rowKey={(a) => `${a.matricula}-${a.codigoVerba}`}
        />
      </Secao>

      <Secao titulo="Campos que este layout não carrega" contagem={relatorio.naoInformaveis.length}>
        <DataTable
          caption="Campos do modelo que este layout não traz, com o motivo e as linhas afetadas"
          columns={COLUNAS_NAO_INFORMAVEIS}
          rows={relatorio.naoInformaveis}
          rowKey={(n) => n.campo}
        />
      </Secao>

      <Secao
        titulo="Totais — origem × gerado"
        contagem={Object.keys(relatorio.totais.origem).length + Object.keys(relatorio.totais.gerado).length}
      >
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-dim)" }}>
          As somas dos dois lados. Cada lado nomeia os totais na própria linguagem — o do órgão, o do padrão —,
          então a conferência é de ordem de grandeza, e não subtração de uma coluna pela outra.
        </p>
        <DataTable
          caption="Somas do arquivo de origem e do pacote gerado, uma linha por total"
          columns={COLUNAS_TOTAIS}
          rows={totaisEmLinhas(relatorio.totais)}
          rowKey={(t) => `${t.lado}-${t.chave}`}
        />
      </Secao>

      <Secao titulo="Avisos" contagem={relatorio.avisos.length}>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
          {relatorio.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </Secao>

      <BaixarJson nome={`relatorio-conversao-${relatorio.competencia}.json`} dados={relatorio} />
    </div>
  );
}

// ============================================================ geração (saída)

type Somada = RelatorioGeracao["dividasSomadas"][number];
type PorCredor = RelatorioGeracao["totaisPorCredor"][number];

const COLUNAS_POR_CREDOR: Column<PorCredor>[] = [
  { key: "credorNome", header: "Credor", wrap: true },
  {
    key: "origem",
    header: "Origem",
    width: 110,
    // "A" e "H" sozinhos nao dizem nada a quem confere o subtotal: um veio de
    // contrato averbado pela Atlas, o outro da carga historica do ente.
    render: (t) => (
      <Pill variant={t.origem === "A" ? "averbado" : "expirado"}>
        {t.origem === "A" ? "averbado" : "histórico"}
      </Pill>
    ),
  },
  { key: "qtdDescontos", header: "Descontos", align: "right", render: (t) => fmtNum(t.qtdDescontos) },
  { key: "somaDescontos", header: "Soma", align: "right", render: (t) => fmtValor(t.somaDescontos) },
];

const COLUNAS_SOMADAS: Column<Somada>[] = [
  { key: "matricula", header: "Matrícula", mono: true },
  { key: "codigoVerba", header: "Verba", mono: true },
  { key: "adfs", header: "ADFs somadas", render: (d) => d.adfs.join(", ") },
  { key: "valorSomado", header: "Valor", align: "right", render: (d) => fmtValor(d.valorSomado) },
  {
    key: "parcela",
    header: "Parcela",
    align: "right",
    render: (d) => `${d.parcelaEscrita}/${d.parcelasTotalEscrito}`,
  },
];

/** O relatório de GERAÇÃO — o que a escrita no layout do ente custou. */
export function RelatorioGeracaoPainel({
  relatorio,
  identidade,
}: {
  relatorio: RelatorioGeracao;
  identidade?: IdentidadeLayout;
}) {
  const adfsSomadas = relatorio.dividasSomadas.reduce((s, d) => s + d.adfs.length, 0);

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
      <Cabecalho
        titulo="Relatório de geração"
        layout={relatorio.layout}
        identidade={identidade}
        competencia={relatorio.competencia}
      />

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", padding: "6px 0" }}>
        <Numero rotulo="linhas no arquivo" valor={relatorio.linhas} />
        <Numero rotulo="dívidas instruídas" valor={relatorio.dividasInstruidas} />
        <Numero rotulo="dívidas somadas" valor={adfsSomadas} tom="alerta" />
        <Numero rotulo="campos sem lugar" valor={relatorio.camposSemCorrespondencia.length} tom="alerta" />
      </div>

      {/* O IDENTIFICADOR É A LINHA DIVISÓRIA entre casar dívida por chave e
          casar por atributo. Um relatório que não dissesse isso deixaria a
          pendência de conciliação aparecer sem explicação, meses depois. */}
      <div style={{ margin: "8px 0", fontSize: 13 }}>
        {relatorio.identificadorEscrito ? (
          <span>
            <Pill variant="averbado">ADF no arquivo</Pill>{" "}
            <span style={{ color: "var(--text-dim)" }}>
              O identificador da dívida foi escrito, então o retorno pode casar de forma exata.
            </span>
          </span>
        ) : (
          <span>
            <Pill variant="pendente">sem ADF no arquivo</Pill>{" "}
            <span style={{ color: "var(--text-dim)" }}>
              Este layout não tem campo para o identificador da dívida. O casamento do retorno cai no caminho
              por matrícula, verba e valor, e o empate vira pendência de conciliação.
            </span>
          </span>
        )}
      </div>

      <Secao titulo="Dívidas somadas numa linha só" contagem={relatorio.dividasSomadas.length} destaque>
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-dim)" }}>
          O layout do órgão aceita uma linha por matrícula e verba, então contratos diferentes do mesmo
          servidor viram um valor só. Aqui está quais foram somados, para a conferência ser possível.
        </p>
        <DataTable
          caption="Dívidas que viraram uma linha só no arquivo do órgão, com matrícula, verba, ADFs somadas e valor"
          columns={COLUNAS_SOMADAS}
          rows={relatorio.dividasSomadas}
          rowKey={(d) => `${d.matricula}-${d.codigoVerba}`}
        />
      </Secao>

      <Secao
        titulo="Campos do modelo sem lugar neste layout"
        contagem={relatorio.camposSemCorrespondencia.length}
      >
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
          {relatorio.camposSemCorrespondencia.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </Secao>

      <Secao titulo="Subtotal por credor" contagem={relatorio.totaisPorCredor.length}>
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-dim)" }}>
          É por aqui que o credor confere o próprio subtotal contra o que ele espera receber da folha.
        </p>
        <DataTable
          caption="Soma dos descontos instruídos por credor, separando o averbado pela Atlas do histórico do órgão"
          columns={COLUNAS_POR_CREDOR}
          rows={relatorio.totaisPorCredor}
          rowKey={(t) => `${t.credorId}-${t.origem}`}
        />
      </Secao>

      <Secao titulo="Avisos" contagem={relatorio.avisos.length}>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
          {relatorio.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </Secao>

      <BaixarJson nome={`relatorio-geracao-${relatorio.competencia}.json`} dados={relatorio} />
    </div>
  );
}

// ============================================================ o modal

/**
 * O RELATÓRIO GUARDADO de um registro do histórico, aberto na tela.
 *
 * Um botão que baixasse o JSON cru cumpriria a letra da tarefa e falharia no
 * ponto: quem opera a folha não abre JSON. O relatório existe para ser LIDO no
 * momento em que a pergunta aparece ("por que a conciliação não fechou?"), e
 * ler exige a tabela de descartadas, não um arquivo no Downloads.
 *
 * O `tipo` do registro decide o painel — o retorno guarda um relatório de
 * conversão, o movimento um de geração. São formatos diferentes, e adivinhar
 * pelo conteúdo daria erro no dia em que um dos dois ficasse vazio.
 *
 * `buscar` entra por parâmetro porque as duas telas leem rotas diferentes com o
 * mesmo formato: a prefeitura vê os arquivos dela, a averbadora vê os de todas.
 */
export function ModalRelatorio({
  arquivo,
  buscar,
  onClose,
}: {
  arquivo: ArquivoTrocado;
  buscar: (id: string) => Promise<RelatorioConversao | RelatorioGeracao>;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["troca-arquivos", "relatorio", arquivo.id],
    queryFn: () => buscar(arquivo.id),
  });

  const titulo =
    arquivo.tipo === "movimento"
      ? `Relatório de geração — ${arquivo.arquivoNome}`
      : `Relatório de conversão — ${arquivo.arquivoNome}`;

  // O NOME DO LAYOUT JÁ ESTÁ NO REGISTRO — é o que foi gravado na hora da troca.
  // O relatório guarda só código e versão, então é daqui que o chip do cabeçalho
  // tira a identidade completa, sem uma consulta a mais ao catálogo.
  const identidade: IdentidadeLayout | undefined = arquivo.layout
    ? { nome: arquivo.layout.nome }
    : undefined;

  return (
    <Modal open onClose={onClose} title={titulo}>
      {q.isLoading ? (
        <p style={{ margin: 0 }}>Carregando o relatório…</p>
      ) : q.isError ? (
        <p style={{ margin: 0, color: "var(--danger, #d33)" }}>
          {(q.error as Error).message}. O relatório fica guardado junto com o arquivo; registros anteriores a
          02/09/2026 não têm um.
        </p>
      ) : arquivo.tipo === "movimento" ? (
        <RelatorioGeracaoPainel relatorio={q.data as RelatorioGeracao} identidade={identidade} />
      ) : (
        <RelatorioConversaoPainel relatorio={q.data as RelatorioConversao} identidade={identidade} />
      )}
    </Modal>
  );
}
